// Morrow — Triggers tab.
//
// Triggers are Morrow's senses: the moments on your apps it watches for — a new
// email, a calendar invite, a mention, a pushed commit. This page is a
// browse-and-turn-on surface, NOT a mirror of your connections: every
// trigger-capable app shows up whether or not it's connected, each trigger
// written as the moment it notices ("When a new email arrives"). Turning one on
// for an app you haven't connected folds the connect step in — connection
// happens *because* you wanted the trigger.
//
// What a trigger "makes happen" is deliberately not wired yet: turning one on
// means Morrow starts noticing that moment, ready to drive a workflow or routine
// once those land. The copy says exactly that — no false promises.
//
// Backed by the person-scoped triggers API (apps/server/src/morrow/triggers):
//   GET  /api/morrow/triggers/available?userId=&toolkit= → an app's trigger types
//   POST /api/morrow/triggers/enable   → turn on (create; needs the app connected)
//   POST /api/morrow/triggers/toggle   → flip an existing trigger
//   POST /api/morrow/triggers/provision→ auto-enable a new app's defaults
//   GET  /api/morrow/composio/connections?userId= → which apps are connected

import {
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
  Link2Icon,
  Loader2Icon,
  SettingsIcon,
  ZapIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";

import { apiUrl } from "../../apiBase";
import { maybeOpenUpgradeFrom402 } from "../../upgradeModal";
import {
  openPendingConnectPopup,
  pointConnectPopup,
  resolveComposioUserId,
  startConnect,
} from "../../composioConnect";
import { cn } from "../../lib/utils";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { useI18n, type MessageKey } from "~/i18n";
import { SettingsPageContainer, SettingsSection } from "./settingsLayout";

type TriggerRow = {
  id: string;
  toolkit: string;
  triggerSlug: string;
  status: string;
  source: string;
  config: string;
};

type AvailableTrigger = {
  slug: string;
  name: string;
  description: string;
  toolkit: string;
  needsConfig: boolean;
  enabled: boolean;
  status: string | null;
  triggerRowId: string | null;
};

// The trigger-capable apps we surface up front — shown whether or not they're
// connected. `watches` is the human one-liner for what Morrow listens for on
// that app; any connected app not in this list is appended generically.
type AppMeta = { slug: string; label: string; watchesKey: MessageKey; logo?: string };

/**
 * Our OWN connectors that can be watched (apps/server/src/morrow/triggers/builtinTriggers.ts).
 *
 * These are not Composio toolkits, so none of them appeared here at all — a seller could not
 * ask to be told when an order landed on the channels they actually sell through. Their logos
 * are local files rather than Composio's logo CDN, which has nothing for them.
 *
 * Entegra and Icecat are deliberately ABSENT and must stay absent: Entegra's order listing
 * marks every order it returns as fetched, so polling it would drain a seller's ERP import
 * queue, and Icecat is a product catalogue with no seller events at all.
 */
const BUILTIN_TRIGGER_APPS: readonly AppMeta[] = [
  {
    slug: "trendyol",
    label: "Trendyol",
    watchesKey: "settings.triggers.genericWatches",
    logo: "/brand/apps/trendyol.png",
  },
  {
    slug: "hepsiburada",
    label: "Hepsiburada",
    watchesKey: "settings.triggers.genericWatches",
    logo: "/brand/apps/hepsiburada.png",
  },
  {
    slug: "n11",
    label: "n11",
    watchesKey: "settings.triggers.genericWatches",
    logo: "/brand/apps/n11.png",
  },
  {
    slug: "ciceksepeti",
    label: "Çiçeksepeti",
    watchesKey: "settings.triggers.genericWatches",
    logo: "/brand/apps/ciceksepeti.png",
  },
  {
    slug: "idefix",
    label: "Idefix",
    watchesKey: "settings.triggers.genericWatches",
    logo: "/brand/apps/idefix.png",
  },
  {
    slug: "pazarama",
    label: "Pazarama",
    watchesKey: "settings.triggers.genericWatches",
    logo: "/brand/apps/pazarama.png",
  },
  {
    slug: "mediamarkt",
    label: "MediaMarkt",
    watchesKey: "settings.triggers.genericWatches",
    logo: "/brand/apps/mediamarkt.png",
  },
  {
    slug: "dia",
    label: "DIA",
    watchesKey: "settings.triggers.genericWatches",
    logo: "/brand/apps/dia.png",
  },
];
const APP_CATALOG: readonly AppMeta[] = [
  { slug: "gmail", label: "Gmail", watchesKey: "settings.triggers.watches.gmail" },
  {
    slug: "googlecalendar",
    label: "Google Calendar",
    watchesKey: "settings.triggers.watches.googlecalendar",
  },
  { slug: "slack", label: "Slack", watchesKey: "settings.triggers.watches.slack" },
  { slug: "github", label: "GitHub", watchesKey: "settings.triggers.watches.github" },
  { slug: "notion", label: "Notion", watchesKey: "settings.triggers.watches.notion" },
  { slug: "linear", label: "Linear", watchesKey: "settings.triggers.watches.linear" },
  {
    slug: "googledrive",
    label: "Google Drive",
    watchesKey: "settings.triggers.watches.googledrive",
  },
  {
    slug: "googlesheets",
    label: "Google Sheets",
    watchesKey: "settings.triggers.watches.googlesheets",
  },
  ...BUILTIN_TRIGGER_APPS,
];
const CATALOG_SLUGS = new Set(APP_CATALOG.map((a) => a.slug));

function prettyToolkit(slug: string): string {
  return slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : slug;
}
function appMetaFor(slug: string): AppMeta {
  return (
    APP_CATALOG.find((a) => a.slug === slug) ?? {
      slug,
      label: prettyToolkit(slug),
      watchesKey: "settings.triggers.genericWatches",
    }
  );
}

/** The app's real Composio logo on a neutral tile (white for crisp contrast in
 * both themes), with a two-letter monogram fallback if the remote image fails. */
function ToolkitLogo({ slug, label }: { slug: string; label: string }) {
  const [failed, setFailed] = useState(false);
  // Our own connectors ship a local logo; Composio's CDN has nothing for them.
  const local = BUILTIN_TRIGGER_APPS.find((a) => a.slug === slug)?.logo;
  if (failed) {
    return (
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-xs font-semibold uppercase text-muted-foreground">
        {label.slice(0, 2)}
      </span>
    );
  }
  return (
    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-white dark:bg-white">
      <img
        src={local ?? `https://logos.composio.dev/api/${slug}`}
        alt={label}
        loading="lazy"
        onError={() => setFailed(true)}
        className="size-5 object-contain"
      />
    </span>
  );
}

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), { headers: { accept: "application/json" } });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}
async function postJson<T>(path: string, body: Record<string, unknown>): Promise<T | null> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (await maybeOpenUpgradeFrom402(res)) return null;
    return (await res.json().catch(() => null)) as T | null;
  } catch {
    return null;
  }
}
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** One trigger, written as the moment it notices, with an on/off switch. */
function TriggerRowView({
  item,
  connected,
  busy,
  onToggle,
}: {
  item: AvailableTrigger;
  connected: boolean;
  busy: boolean;
  onToggle: () => void;
}) {
  const { t } = useI18n();
  const needsSetup = item.needsConfig && !item.triggerRowId;
  const blocked = needsSetup; // config-required triggers wait for a setup UI
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
          {t("settings.triggers.when")}
        </div>
        <div className="mt-0.5 flex items-center gap-2">
          <span className="truncate text-xs font-medium text-foreground">{item.name}</span>
          {needsSetup ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-600 dark:text-amber-400">
              <SettingsIcon className="size-2.5" /> {t("settings.triggers.needsSetup")}
            </span>
          ) : null}
        </div>
        {item.description ? (
          <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">
            {item.description}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={item.enabled}
        aria-label={
          item.enabled
            ? t("settings.triggers.turnOff", { name: item.name })
            : t("settings.triggers.turnOn", { name: item.name })
        }
        disabled={blocked || busy}
        onClick={onToggle}
        className={cn(
          "relative mt-1 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
          item.enabled ? "bg-emerald-500" : "bg-muted-foreground/30",
          (blocked || busy) && "cursor-not-allowed opacity-50",
        )}
        title={
          blocked
            ? t("settings.triggers.needsValueFirst")
            : connected
              ? undefined
              : t("settings.triggers.connectAppFirst")
        }
      >
        {busy ? (
          <Loader2Icon className="absolute left-1/2 top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 animate-spin text-white" />
        ) : (
          <span
            className={cn(
              "inline-block size-4 transform rounded-full bg-white shadow transition-transform",
              item.enabled ? "translate-x-4" : "translate-x-0.5",
            )}
          />
        )}
      </button>
    </div>
  );
}

/** An app's trigger types, lazy-loaded on first expand. Handles turning a
 * trigger on — connecting the app first if needed. */
function AppTriggerList({
  userId,
  toolkit,
  connected,
  onConnectRequest,
  onChanged,
}: {
  userId: string;
  toolkit: string;
  connected: boolean;
  onConnectRequest: (triggerSlug: string) => Promise<boolean>;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const [items, setItems] = useState<AvailableTrigger[] | null>(null);
  const [busySlug, setBusySlug] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const data = await getJson<{ triggers?: AvailableTrigger[] }>(
      `/api/morrow/triggers/available?userId=${encodeURIComponent(userId)}&toolkit=${encodeURIComponent(toolkit)}`,
    );
    setItems(Array.isArray(data?.triggers) ? data!.triggers : []);
  }, [toolkit, userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const toggle = useCallback(
    async (item: AvailableTrigger) => {
      if (item.needsConfig && !item.triggerRowId) return;
      setBusySlug(item.slug);
      if (!connected) {
        // Fold the connect step in: connect the app, then enable this trigger.
        const ok = await onConnectRequest(item.slug);
        if (ok) await reload();
        setBusySlug(null);
        onChanged();
        return;
      }
      if (item.triggerRowId) {
        await postJson("/api/morrow/triggers/toggle", {
          userId,
          triggerRowId: item.triggerRowId,
          enabled: !item.enabled,
        });
      } else {
        await postJson("/api/morrow/triggers/enable", {
          userId,
          toolkit,
          triggerSlug: item.slug,
          config: {},
        });
      }
      await reload();
      setBusySlug(null);
      onChanged();
    },
    [connected, onChanged, onConnectRequest, reload, toolkit, userId],
  );

  if (items === null) {
    return (
      <div className="flex items-center justify-center py-6 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <p className="px-1 py-3 text-xs text-muted-foreground">
        {t("settings.triggers.noTriggersForApp")}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1.5 pt-1">
      {!connected ? (
        <p className="pb-1 text-[11px] text-muted-foreground">
          {t("settings.triggers.connectThenListen", { app: appMetaFor(toolkit).label })}
        </p>
      ) : null}
      {items.map((item) => (
        <TriggerRowView
          key={item.slug}
          item={item}
          connected={connected}
          busy={busySlug === item.slug}
          onToggle={() => void toggle(item)}
        />
      ))}
    </div>
  );
}

function AppCard({
  userId,
  meta,
  connected,
  enabledCount,
  expanded,
  onExpandToggle,
  onConnectRequest,
  onChanged,
}: {
  userId: string;
  meta: AppMeta;
  connected: boolean;
  enabledCount: number;
  expanded: boolean;
  onExpandToggle: () => void;
  onConnectRequest: (triggerSlug: string) => Promise<boolean>;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={onExpandToggle}
        className="flex items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="flex min-w-0 items-center gap-3">
          <ToolkitLogo slug={meta.slug} label={meta.label} />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">{meta.label}</span>
              {enabledCount > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-600 dark:text-emerald-400">
                  <CheckIcon className="size-2.5" />{" "}
                  {t("settings.triggers.enabledCount", { count: enabledCount })}
                </span>
              ) : connected ? (
                <span className="rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {t("settings.triggers.connected")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground/70">
                  <Link2Icon className="size-2.5" /> {t("settings.triggers.notConnected")}
                </span>
              )}
            </div>
            <div className="mt-0.5 truncate text-xs text-muted-foreground">
              {t(meta.watchesKey)}
            </div>
          </div>
        </div>
        <ChevronDownIcon
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            expanded && "rotate-180",
          )}
        />
      </button>
      {expanded ? (
        <div className="border-t border-border/60 px-4 pb-4 pt-2">
          <AppTriggerList
            userId={userId}
            toolkit={meta.slug}
            connected={connected}
            onConnectRequest={onConnectRequest}
            onChanged={onChanged}
          />
        </div>
      ) : null}
    </div>
  );
}

// ── WhatsApp: a personal (linked-device) channel, not a Composio toolkit, so it
// gets its own card. Its "triggers" are inbound-message rules that each RUN a saved
// workflow — one WhatsApp link can drive many workflows. Distinct API + state from
// the Composio cards above; kept self-contained so it doesn't touch that logic.
type WaTriggerRow = {
  id: string;
  name: string;
  matchKind: "all" | "contact" | "keyword" | string;
  matchValue: string;
  enabled: boolean;
  workflowId: string | null;
};
type WorkflowLite = { id: string; name: string };

function describeMatch(
  row: WaTriggerRow,
  translate: (key: MessageKey, vars?: Record<string, string>) => string,
): string {
  if (row.matchKind === "contact") {
    return translate("settings.triggers.whatsapp.match.fromContact", {
      value: row.matchValue || translate("settings.triggers.whatsapp.match.aContact"),
    });
  }
  if (row.matchKind === "keyword") {
    return translate("settings.triggers.whatsapp.match.contains", { value: row.matchValue });
  }
  return translate("settings.triggers.whatsapp.match.all");
}

function WhatsAppTriggerCard({
  expanded,
  onExpandToggle,
}: {
  expanded: boolean;
  onExpandToggle: () => void;
}) {
  const { t } = useI18n();
  const [waStatus, setWaStatus] = useState<string | null>(null);
  const [triggers, setTriggers] = useState<WaTriggerRow[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowLite[]>([]);
  const [matchKind, setMatchKind] = useState<"all" | "contact" | "keyword">("all");
  const [matchValue, setMatchValue] = useState("");
  const [workflowId, setWorkflowId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [st, tr, wf] = await Promise.all([
      getJson<{ status?: string }>("/api/morrow/whatsapp-personal/status"),
      getJson<{ triggers?: WaTriggerRow[] }>("/api/morrow/whatsapp-triggers"),
      // Lite by name and by payload: this list is only ever used to label a trigger's workflow.
      getJson<{ workflows?: WorkflowLite[] }>("/api/morrow/workflows?brief=1"),
    ]);
    setWaStatus(st?.status ?? "disconnected");
    setTriggers(Array.isArray(tr?.triggers) ? tr!.triggers : []);
    setWorkflows(Array.isArray(wf?.workflows) ? wf!.workflows : []);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const linked = waStatus === "connected";
  const enabledCount = triggers.filter((t) => t.enabled).length;
  const wfName = useCallback(
    (id: string | null) =>
      workflows.find((w) => w.id === id)?.name ?? t("settings.triggers.whatsapp.aWorkflow"),
    [t, workflows],
  );

  const addTrigger = useCallback(async () => {
    if (!workflowId) return;
    setBusy(true);
    const wf = workflows.find((w) => w.id === workflowId);
    await postJson("/api/morrow/whatsapp-triggers", {
      name: `${wf?.name ?? "Workflow"} · WhatsApp`,
      workflowId,
      matchKind,
      matchValue: matchValue.trim(),
    });
    setBusy(false);
    setMatchValue("");
    setMatchKind("all");
    setWorkflowId("");
    await load();
  }, [workflowId, workflows, matchKind, matchValue, load]);

  const toggle = useCallback(
    async (t: WaTriggerRow) => {
      await postJson("/api/morrow/whatsapp-triggers/toggle", { id: t.id, enabled: !t.enabled });
      await load();
    },
    [load],
  );
  const remove = useCallback(
    async (id: string) => {
      await postJson("/api/morrow/whatsapp-triggers/delete", { id });
      await load();
    },
    [load],
  );

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card">
      <button
        type="button"
        onClick={onExpandToggle}
        className="flex items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="flex min-w-0 items-center gap-3">
          <ToolkitLogo slug="whatsapp" label="WhatsApp" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">WhatsApp</span>
              {enabledCount > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-600 dark:text-emerald-400">
                  <CheckIcon className="size-2.5" />{" "}
                  {t("settings.triggers.enabledCount", { count: enabledCount })}
                </span>
              ) : linked ? (
                <span className="rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {t("settings.triggers.whatsapp.linked")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground/70">
                  <Link2Icon className="size-2.5" /> {t("settings.triggers.whatsapp.notLinked")}
                </span>
              )}
            </div>
            <div className="mt-0.5 truncate text-xs text-muted-foreground">
              {t("settings.triggers.whatsapp.summary")}
            </div>
          </div>
        </div>
        <ChevronDownIcon
          className={cn(
            "size-4 shrink-0 text-muted-foreground transition-transform",
            expanded && "rotate-180",
          )}
        />
      </button>
      {expanded ? (
        <div className="border-t border-border/60 px-4 pb-4 pt-3">
          {!linked ? (
            <p className="text-xs text-muted-foreground">
              {t("settings.triggers.whatsapp.linkFirstBefore")}{" "}
              <Link to="/settings/whatsapp-connect" className="underline hover:text-foreground">
                {t("settings.triggers.whatsapp.settingsWhatsApp")}
              </Link>
              {t("settings.triggers.whatsapp.linkFirstAfter")}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {/* Existing rules */}
              {triggers.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  {triggers.map((row) => (
                    <div
                      key={row.id}
                      className="flex items-start justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5"
                    >
                      <div className="min-w-0">
                        <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
                          {t("settings.triggers.when")}
                        </div>
                        <div className="mt-0.5 truncate text-xs font-medium text-foreground">
                          {describeMatch(row, t)}
                        </div>
                        <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                          {t("settings.triggers.whatsapp.runs", { name: wfName(row.workflowId) })}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={row.enabled}
                          onClick={() => void toggle(row)}
                          className={cn(
                            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                            row.enabled ? "bg-emerald-500" : "bg-muted-foreground/30",
                          )}
                        >
                          <span
                            className={cn(
                              "inline-block size-4 transform rounded-full bg-white shadow transition-transform",
                              row.enabled ? "translate-x-4" : "translate-x-0.5",
                            )}
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() => void remove(row.id)}
                          className="text-[11px] text-muted-foreground hover:text-foreground"
                        >
                          {t("settings.triggers.whatsapp.remove")}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">
                  {t("settings.triggers.whatsapp.noRules")}
                </p>
              )}

              {/* Add a rule */}
              {workflows.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  {t("settings.triggers.whatsapp.createWorkflowBefore")}{" "}
                  <Link to="/workflows" className="underline hover:text-foreground">
                    {t("settings.triggers.whatsapp.workflowLink")}
                  </Link>{" "}
                  {t("settings.triggers.whatsapp.createWorkflowAfter")}
                </p>
              ) : (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border/60 p-2.5">
                  <select
                    className="rounded-md border border-border bg-transparent px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/40"
                    value={matchKind}
                    onChange={(e) => setMatchKind(e.target.value as "all" | "contact" | "keyword")}
                  >
                    <option value="all">{t("settings.triggers.whatsapp.match.all")}</option>
                    <option value="contact">{t("settings.triggers.whatsapp.match.contact")}</option>
                    <option value="keyword">{t("settings.triggers.whatsapp.match.keyword")}</option>
                  </select>
                  {matchKind !== "all" ? (
                    <input
                      className="min-w-[8rem] flex-1 rounded-md border border-border bg-transparent px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/40"
                      placeholder={matchKind === "contact" ? "number / jid" : "keyword"}
                      value={matchValue}
                      onChange={(e) => setMatchValue(e.target.value)}
                    />
                  ) : null}
                  <span className="text-[11px] text-muted-foreground">
                    {t("settings.triggers.whatsapp.run")}
                  </span>
                  <select
                    className="rounded-md border border-border bg-transparent px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/40"
                    value={workflowId}
                    onChange={(e) => setWorkflowId(e.target.value)}
                  >
                    <option value="">{t("settings.triggers.whatsapp.chooseWorkflow")}</option>
                    {workflows.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => void addTrigger()}
                    disabled={busy || !workflowId}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
                  >
                    {busy ? (
                      <Loader2Icon className="size-3.5 animate-spin" />
                    ) : (
                      t("settings.triggers.whatsapp.addRule")
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function TriggersSettings() {
  const { t } = useI18n();
  const { isLoaded: isClerkLoaded, isSignedIn, userId: clerkUserId } = useMorrowClerkAuth();
  const userId = useMemo(
    () => resolveComposioUserId(isClerkLoaded && isSignedIn ? clerkUserId : null),
    [isClerkLoaded, isSignedIn, clerkUserId],
  );

  const [connected, setConnected] = useState<string[] | null>(null);
  const [rows, setRows] = useState<TriggerRow[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const provisionedRef = useRef(false);

  const loadState = useCallback(async () => {
    if (!userId) return;
    // Built-in apps live in their OWN connections table, not Composio's, so both have to be
    // read or every marketplace here would render as "not connected" and refuse to turn on.
    const [conn, tr, builtin] = await Promise.all([
      getJson<{ connected?: string[] }>(
        `/api/morrow/composio/connections?userId=${encodeURIComponent(userId)}`,
      ),
      getJson<{ triggers?: TriggerRow[] }>(
        `/api/morrow/triggers?userId=${encodeURIComponent(userId)}`,
      ),
      getJson<{ connections?: { appId?: string; status?: string }[] }>(
        `/api/morrow/apps/connections`,
      ),
    ]);
    const composio = conn && Array.isArray(conn.connected) ? conn.connected : [];
    const builtinConnected = (builtin?.connections ?? [])
      .filter((c) => typeof c.appId === "string" && c.status !== "error")
      .map((c) => c.appId as string);
    setConnected([...new Set([...composio, ...builtinConnected])]);
    if (tr && Array.isArray(tr.triggers)) setRows(tr.triggers);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const run = async () => {
      if (!provisionedRef.current) {
        provisionedRef.current = true;
        await postJson("/api/morrow/triggers/provision", { userId });
      }
      await loadState();
    };
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Connect an app (OAuth popup) then optionally enable the trigger the user
  // clicked. Returns true once the app is connected (and the trigger enabled).
  const connectAndEnable = useCallback(
    async (toolkit: string, triggerSlug: string): Promise<boolean> => {
      if (!userId) return false;
      const popup = openPendingConnectPopup();
      const outcome = await startConnect(toolkit, userId);
      if (outcome.kind === "failed") {
        popup?.close();
        return false;
      }
      if (outcome.kind === "redirect") {
        pointConnectPopup(popup, outcome.url);
        // Wait for the OAuth popup to complete out-of-band; poll the connections
        // list until this toolkit shows up (bounded ~90s).
        let ok = false;
        for (let i = 0; i < 45; i++) {
          await wait(2000);
          const conn = await getJson<{ connected?: string[] }>(
            `/api/morrow/composio/connections?userId=${encodeURIComponent(userId)}`,
          );
          if (conn?.connected?.includes(toolkit)) {
            ok = true;
            break;
          }
        }
        if (!ok) return false;
      } else {
        popup?.close();
      }
      await postJson("/api/morrow/triggers/enable", { userId, toolkit, triggerSlug, config: {} });
      await loadState();
      return true;
    },
    [loadState, userId],
  );

  const enabledByToolkit = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of rows) {
      if (row.status !== "enabled") continue;
      map.set(row.toolkit, (map.get(row.toolkit) ?? 0) + 1);
    }
    return map;
  }, [rows]);

  // Catalog first (in curated order), then any connected app not in the catalog.
  const appSlugs = useMemo(() => {
    const extra = (connected ?? []).filter((s) => !CATALOG_SLUGS.has(s));
    return [...APP_CATALOG.map((a) => a.slug), ...extra];
  }, [connected]);

  const connectedSet = useMemo(() => new Set(connected ?? []), [connected]);
  const totalOn = useMemo(
    () => [...enabledByToolkit.values()].reduce((a, b) => a + b, 0),
    [enabledByToolkit],
  );

  return (
    <SettingsPageContainer>
      <SettingsSection
        id="triggers"
        title={t("settings.section.triggers")}
        icon={<ZapIcon className="size-4.5" />}
      >
        <div className="px-3 pb-2 sm:px-4">
          <p className="text-sm text-muted-foreground">{t("settings.triggers.intro")}</p>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground/80">
            <ArrowRightIcon className="size-3.5 shrink-0" />
            {t("settings.triggers.introFollowUp")}
            {totalOn > 0 ? (
              <span className="font-medium text-foreground">
                {" "}
                {t("settings.triggers.listeningNow", { count: totalOn })}
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex flex-col gap-3 px-3 sm:px-4">
          {connected === null ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2Icon className="size-5 animate-spin" />
            </div>
          ) : (
            <>
              <WhatsAppTriggerCard
                expanded={expanded === "whatsapp"}
                onExpandToggle={() =>
                  setExpanded((cur) => (cur === "whatsapp" ? null : "whatsapp"))
                }
              />
              {appSlugs.map((slug) => (
                <AppCard
                  key={slug}
                  userId={userId}
                  meta={appMetaFor(slug)}
                  connected={connectedSet.has(slug)}
                  enabledCount={enabledByToolkit.get(slug) ?? 0}
                  expanded={expanded === slug}
                  onExpandToggle={() => setExpanded((cur) => (cur === slug ? null : slug))}
                  onConnectRequest={(triggerSlug) => connectAndEnable(slug, triggerSlug)}
                  onChanged={() => void loadState()}
                />
              ))}
            </>
          )}
        </div>
      </SettingsSection>
    </SettingsPageContainer>
  );
}
